import { Flex, FlexProps } from '@chakra-ui/react'
import Color from 'color'
import { toaster } from 'components'
import { isEqual } from 'lodash-es'
import QRCodeStyling, { DownloadOptions, Options } from 'qr-code-styling'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { QRCodeDotStyle } from 'types'

export interface Props
  extends Omit<FlexProps, 'children' | 'color' | 'backgroundColor'>, QRCodeProps {}

interface QRCodeProps {
  color?: string
  data: string
  dotStyle?: QRCodeDotStyle
  size: number
}

export interface QRCodeViewerAPI {
  copyToClipboard: () => Promise<void>
  download: (options?: Partial<DownloadOptions>) => Promise<void>
}

const QRCodeViewer = forwardRef<QRCodeViewerAPI, Props>((props, api) => {
  const qrRef = useRef<HTMLDivElement>(null)

  const [qrCode, setQrCode] = useState<QRCodeStyling>()

  const { data, color = 'black', dotStyle = 'rounded', size, ...flexProps } = props
  const inputs = { data, color, size: size - 20, dotStyle }
  const lastInputs = useRef<typeof inputs>(null)
  const backgroundColor = Color(color).isDark() ? '#fff' : '#000'

  useEffect(() => {
    if (isEqual(lastInputs.current, inputs) || !inputs.size) return

    lastInputs.current = inputs
    const options = makeOptions(lastInputs.current)
    if (qrCode) return qrCode.update(options)

    makeQRCode(options).then(generated => {
      setQrCode(generated)
      generated.append(qrRef.current!)
    })
  }, [inputs])

  useImperativeHandle(api, () => ({
    copyToClipboard: async () => {
      const qrCode = await makeQRCode(makeOptions({ ...inputs, size: 500 }))
      const svgElement = await qrCode.getRawData('png')
      if (!svgElement) return

      const blob =
        svgElement instanceof Blob
          ? svgElement
          : new Blob([svgElement as any], { type: 'image/png' })
      const item = new ClipboardItem({ 'image/png': blob })
      await navigator.clipboard.write([item])

      toaster.create({ title: 'QR Code copied to clipboard', type: 'success' })
    },
    download: async (options?: Partial<DownloadOptions>) => {
      const qrCode = await makeQRCode(makeOptions({ ...inputs, size: 500 }))
      qrCode?.download({ extension: 'png', name: 'QR Code', ...options })
    },
  }))

  return (
    <Flex
      align="center"
      backgroundColor={backgroundColor}
      borderRadius="md"
      height={size}
      justify="center"
      overflow="hidden"
      width={size}
      {...flexProps}
    >
      <Flex ref={qrRef} />
    </Flex>
  )
})

function makeOptions(inputs: QRCodeProps & { size: number }): Options {
  const { color = 'black', size, dotStyle, ...rest } = inputs

  return {
    backgroundOptions: { color: 'transparent' },
    dotsOptions: { color, type: dotStyle },
    margin: 0,
    cornersSquareOptions: { color, type: dotStyle === 'square' ? 'square' : 'extra-rounded' },
    cornersDotOptions: { color, type: dotStyle },
    height: size,
    type: 'svg',
    width: size,
    ...rest,
  }
}

export async function makeQRCode(options: Options) {
  const QRCodeStyling = (await import('qr-code-styling')).default
  const qrCode = new QRCodeStyling(options)
  return qrCode
}

export default QRCodeViewer
